import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import type {
  DataGridProProps,
  GridAlignment,
  GridColDef,
  GridColTypeDef,
  GridColumnHeaderParams,
} from '@mui/x-data-grid-pro';
import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { Fragment, useMemo } from 'react';
import { ImageMediaCell } from '@/features/DataGrid';
import { selectCurrency } from '@/entities/session';
import { DEFAULT_COLUMN_WIDTH } from '@/shared/constants';
import type { RowHeights } from '@/shared/constants';
import { parseMediaColumnValue } from '@/shared/data';
import { useNumberFormatter } from '@/shared/hooks';
import { formatDate } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import { DimensionColumnDataType, toFormatType } from '@/shared/types';
import type {
  ColumnsConfig,
  MetricColumnDataType,
  MetricDataFormatType,
} from '@/shared/types';
import type { TotalData } from '@/shared/ui';
import { ValueType, type Highlight } from '../../model/types';
import { Cell } from '../Cell/Cell';
import { DATE_COLUMN_MIN_WIDTH } from '../DataTable/constants';
import { DateTableHeader } from '../DateTableHeader/DateTableHeader';

const DEFAULT_GROUPING_COLUMN_WIDTH = 300;

interface Params {
  total?: TotalData;
  rowHeight: RowHeights;
  columnsConfig: ColumnsConfig;
  columnsWidth: Record<string, number | undefined>;
  dimensions: string[];
  metric: string;
  highlight: Highlight;
  dataColumns: string[];
}

export const useTreeDataGridProps = ({
  total,
  rowHeight,
  columnsConfig,
  columnsWidth,
  dimensions,
  metric,
  highlight,
  dataColumns,
}: Params): {
  columns: GridColDef[];
  groupingColDef: DataGridProProps['groupingColDef'];
  getTreeDataPath: DataGridProProps['getTreeDataPath'];
} => {
  const userCurrency = useAppSelector(selectCurrency);
  const formattersMap = useNumberFormatter(userCurrency);
  const columns: GridColDef[] = useMemo(
    () => {
      const metricConfig = columnsConfig[metric];
      const metricFormatter =
        formattersMap[metricConfig.type as MetricColumnDataType][
          toFormatType(metricConfig.dataType as MetricDataFormatType)
        ];

      return dataColumns.map((column) => {
        return {
          ...getMetricTypeProps({
            total,
            metric,
            formatter: metricFormatter,
            highlight,
            rowHeight,
          }),
          field: column,
          minWidth: DATE_COLUMN_MIN_WIDTH,
          width: columnsWidth[column] || DEFAULT_COLUMN_WIDTH,
          disableReorder: true,
          align: 'right' as GridAlignment,
          headerClassName: 'metricHeader',
          cellClassName: 'metricCell',
          renderHeader: ({ colDef }: GridColumnHeaderParams) => {
            if (!colDef) {
              return null;
            }

            return <DateTableHeader date={colDef.field} />;
          },
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dataColumns,
      metric,
      highlight,
      total,
      columnsConfig,
      columnsWidth,
      rowHeight,
    ],
  );

  return {
    columns,
    getTreeDataPath: (row) => row.path,
    groupingColDef: {
      headerClassName: 'groupingHeader',
      cellClassName: 'groupingCell',
      hideDescendantCount: true,
      renderHeader: () => {
        const tooltipTitle = dimensions
          .map((dimension) => {
            return columnsConfig[dimension].name;
          })
          .join(' ⮑ ');

        return (
          <Tooltip title={tooltipTitle}>
            <Box
              component="span"
              sx={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {dimensions.map((dimension, index) => {
                return (
                  <Fragment key={dimension}>
                    {index !== 0 && (
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.66rem', mx: 0.5 }}
                      >
                        ⮑
                      </Typography>
                    )}
                    {columnsConfig[dimension].name}
                  </Fragment>
                );
              })}
            </Box>
          </Tooltip>
        );
      },
      valueGetter: (_, row) => {
        if (row.isTotal) {
          return 'Total';
        }

        const prop = dimensions[row.path.length - 1];

        if (!prop) {
          return <Skeleton width={50} />;
        }

        const type = columnsConfig[prop].type;
        const value = row[prop];

        switch (type) {
          case DimensionColumnDataType.URL:
            try {
              const url = new URL(value);

              return (
                <a href={url.toString()} target="_blank" rel="noreferrer">
                  {value}
                </a>
              );
            } catch {
              return value;
            }
          case DimensionColumnDataType.DATE:
            return formatDate(value);
          case DimensionColumnDataType.MEDIA: {
            if (!value) {
              return value;
            }

            const {
              type,
              value: mediaName,
              url,
            } = parseMediaColumnValue(value);

            if (type === 'image') {
              return <ImageMediaCell label={mediaName} url={url} />;
            }

            return mediaName;
          }
          default:
            return value;
        }
      },
      width:
        columnsWidth[GRID_TREE_DATA_GROUPING_FIELD] ||
        DEFAULT_GROUPING_COLUMN_WIDTH,
    },
  };
};

const getMetricTypeProps = ({
  total,
  metric,
  formatter,
  highlight,
  rowHeight,
}: {
  total?: TotalData;
  metric: string;
  formatter: (v: number) => string;
  highlight: Highlight;
  rowHeight: RowHeights;
}): GridColTypeDef => {
  return {
    type: 'number',
    renderCell: (params) => {
      const { field, row, api } = params;

      if (row.isTotal) {
        return formatter(params.value);
      }

      let totalValue = total?.[field];

      if (
        highlight.valueType === ValueType.percent &&
        params.rowNode.parent &&
        params.rowNode.parent !== 'auto-generated-group-node-root'
      ) {
        const parentRow = api.getRow(params.rowNode.parent);

        totalValue = parentRow[metric]?.[field];
      }

      return (
        <Cell
          rowHeight={rowHeight}
          formatter={formatter}
          value={row[metric]?.[field]}
          percent={totalValue ? row[metric]?.[field] / totalValue : undefined}
        />
      );
    },
  };
};
